import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Play, Pause, SkipForward, SkipBack, BookOpen, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api, { API } from '../lib/api';

const fmt = (s) => {
  if (!s || Number.isNaN(s)) return '0:00';
  const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

export default function AudiobookPlayer() {
  const [book, setBook] = useState(null);
  const [chapterIdx, setChapterIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(0);
  const [audioError, setAudioError] = useState(false);
  const [saved, setSaved] = useState(false);
  const audioRef = useRef(null);
  const lastSaveRef = useRef(0);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/audiobook/chapters');
      setBook(data);
      const idx = data.progress?.chapter_index ?? 0;
      setChapterIdx(idx);
      // Restore position on first mount
      setTimeout(() => {
        if (audioRef.current && data.progress?.position_seconds) {
          audioRef.current.currentTime = data.progress.position_seconds;
        }
      }, 300);
    } catch { toast.error('Could not load audiobook'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveProgress = useCallback(async (ci, pos) => {
    try { await api.post('/audiobook/progress', { chapter_index: ci, position_seconds: Math.floor(pos) }); }
    catch { /* silent */ }
  }, []);

  useEffect(() => {
    const a = audioRef.current; if (!a) return;
    const onTime = () => {
      setTime(a.currentTime);
      if (a.currentTime - lastSaveRef.current > 5) {
        lastSaveRef.current = a.currentTime;
        saveProgress(chapterIdx, a.currentTime);
        setSaved(true); setTimeout(() => setSaved(false), 1200);
      }
    };
    const onLoaded = () => setDur(a.duration || 0);
    const onEnded = () => {
      const next = chapterIdx + 1;
      if (book && next < book.chapters.length) {
        setChapterIdx(next); setPlaying(true);
      } else { setPlaying(false); }
    };
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onLoaded);
    a.addEventListener('ended', onEnded);
    const onErr = () => setAudioError(true);
    const onCanPlay = () => setAudioError(false);
    a.addEventListener('error', onErr);
    a.addEventListener('canplay', onCanPlay);
    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onLoaded);
      a.removeEventListener('ended', onEnded);
      a.removeEventListener('error', onErr);
      a.removeEventListener('canplay', onCanPlay);
    };
  }, [book, chapterIdx, saveProgress]);

  useEffect(() => {
    const a = audioRef.current; if (!a) return;
    if (playing) { a.play().catch(() => setPlaying(false)); } else { a.pause(); }
  }, [playing, chapterIdx]);

  if (!book) return <div className="text-navy/60 text-center py-8">Loading audiobook…</div>;

  const current = book.chapters[chapterIdx];
  const audioUrl = `${backendUrl}${current.url}`;
  const pct = dur ? (time / dur) * 100 : 0;

  const seek = (e) => {
    if (!audioRef.current || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const p = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = p * dur;
  };
  const jump = (delta) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, Math.min(dur, audioRef.current.currentTime + delta));
  };
  const pickChapter = (i) => {
    setChapterIdx(i); setTime(0);
    setTimeout(() => { if (audioRef.current) audioRef.current.currentTime = 0; setPlaying(true); }, 100);
  };

  return (
    <section className="space-y-4" data-testid="audiobook-player">
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      {/* Now playing card */}
      <div className="rounded-3xl overflow-hidden shadow-squishyLg border-2 border-gold" data-testid="audiobook-now-playing">
        <div className="bg-gradient-to-br from-navy via-navy to-teal p-6 text-white">
          <div className="flex items-start gap-4">
            <div className="w-24 h-32 rounded-2xl bg-gradient-to-br from-gold to-teal flex items-center justify-center shrink-0 shadow-squishyLg">
              <div className="text-center">
                <div className="text-3xl">🏺</div>
                <div className="font-heading text-xs font-bold text-navy mt-1 leading-tight px-1">The Secrets<br/>of Egypt</div>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-gold text-xs font-heading font-bold uppercase tracking-wider">Now Playing</div>
              <h3 className="font-heading text-xl font-bold mt-1 leading-tight" data-testid="current-chapter-title">Chapter {current.number}</h3>
              <div className="text-white/80 text-sm mt-0.5">{current.title}</div>
              <div className="text-white/50 text-[11px] mt-2">{book.book_subtitle}</div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-5 select-none" onClick={seek} data-testid="audiobook-progress-bar">
            <div className="h-2 bg-white/15 rounded-full overflow-hidden cursor-pointer">
              <div className="h-full bg-gradient-to-r from-gold to-teal rounded-full transition-[width] duration-100" style={{ width: `${pct}%` }} />
            </div>
            <div className="flex items-center justify-between text-xs font-heading mt-1.5 text-white/70">
              <span data-testid="audio-time">{fmt(time)}</span>
              {saved && <span className="text-gold text-[10px] uppercase tracking-wider">✓ saved</span>}
              <span data-testid="audio-duration">{fmt(dur)}</span>
            </div>
          </div>

          {/* Controls */}
          <div className="mt-4 flex items-center justify-center gap-4">
            <button onClick={() => jump(-15)} className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" data-testid="audio-back-15" aria-label="Back 15s">
              <SkipBack className="w-5 h-5" />
            </button>
            <button onClick={() => setPlaying((p) => !p)} disabled={audioError}
              className="w-16 h-16 rounded-full bg-gold text-navy shadow-gold flex items-center justify-center hover:scale-105 active:scale-95 transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="audio-play-pause" aria-label={playing ? 'Pause' : 'Play'}>
              {playing ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 ml-1" />}
            </button>
            <button onClick={() => jump(15)} className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" data-testid="audio-fwd-15" aria-label="Forward 15s">
              <SkipForward className="w-5 h-5" />
            </button>
          </div>
          {audioError && (
            <div className="mt-4 text-center text-xs text-gold bg-white/10 rounded-2xl p-3" data-testid="audio-unavailable-notice">
              🎧 Audio file for this chapter is being prepared. Drop the MP3s into <code className="font-mono">/app/backend/audiobook/</code> and refresh to listen.
            </div>
          )}
        </div>
      </div>

      {/* Chapter list */}
      <div className="bg-white rounded-3xl border-2 border-cream shadow-squishy p-5" data-testid="audiobook-chapter-list">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="w-5 h-5 text-teal" />
          <h3 className="font-heading text-lg font-bold text-navy">Chapters</h3>
        </div>
        <div className="space-y-2">
          {book.chapters.map((c) => {
            const active = c.index === chapterIdx;
            return (
              <button key={c.index} onClick={() => pickChapter(c.index)}
                className={`w-full flex items-center gap-3 p-4 rounded-2xl border-2 text-left transition-colors duration-200 ${active ? 'border-teal bg-teal/10' : 'border-cream hover:border-teal/50'}`}
                data-testid={`chapter-row-${c.index}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 font-heading font-bold ${active ? 'bg-gold text-navy' : 'bg-cream text-navy'}`}>
                  {active && playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-navy text-sm">Chapter {c.number}</div>
                  <div className="text-navy/60 text-xs truncate">{c.title}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-navy/30 shrink-0" />
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
